import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { resolvePrintError } from "../../actions/printError";
import {
  errorCodeLabel,
  statusOf,
  money,
  pagesText,
  orderText,
  fileOf,
  dateText,
  customerPrintedOf,
  refundFromCount,
  suggestedRefundOf,
  customerCountNote,
  accountLabel,
  accountText,
} from "./printErrorLabels";
import CopyButton from "./CopyButton";

const inputClass =
  "block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 border-purple-100 rounded-lg dark:bg-gray-700 focus:border-purple-200 focus:outline-none focus:ring-purple-200 focus:ring-2 dark:focus:shadow-outline-gray";

const secondaryButton =
  "flex-1 px-5 py-3 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 border border-gray-300 rounded-lg dark:text-gray-400 sm:flex-none sm:px-4 sm:py-2 active:bg-transparent hover:border-gray-500 focus:border-gray-500 active:text-gray-500 focus:outline-none focus:shadow-outline-gray";

const primaryButton =
  "flex-1 px-5 py-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg sm:flex-none sm:px-4 sm:py-2 active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple dark:text-gray-300";

const NOTE_MAX = 1000;

const ACTIONS = [
  {
    key: "refunded",
    label: "Буцаалт хийсэн",
    help: "Хэрэглэгчид мөнгийг нь буцааж шилжүүлсэн.",
  },
  {
    key: "rejected",
    label: "Татгалзах",
    help: "Буцаалт хийхгүй.",
  },
  {
    key: "resolved",
    label: "Хэвлэгдсэн",
    help: "Эцэст нь хэвлэгдсэн тул буцаах зүйл байхгүй.",
  },
];

// A print paid from a page bundle gets its pages back in the bundle, at once, rather
// than money sent by hand
const BUNDLE_REFUND_HELP =
  "Хэвлэгдээгүй хуудсыг багцад нь буцааж нэмнэ. Хэрэглэгчид мэйл, мэдэгдэл очно.";

// A bundle keeps half pages, which the backend calls units
const pagesOfUnits = (units) => (units === undefined || units === null ? null : units / 2);
const pagesLabel = (pages) => (pages === null ? "—" : String(pages).replace(".5", ",5") + " хуудас");

const detailRow = (label, value) => (
  <div className="flex justify-between py-1">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="ml-4 text-right text-gray-700 break-all dark:text-gray-300">
      {value}
    </span>
  </div>
);

const phoneLink = (phone) => (
  <a
    href={"tel:" + phone}
    className="font-semibold text-purple-600 dark:text-purple-400 hover:underline"
  >
    {phone}
  </a>
);

// Opens on a pending print error. Saving only records the decision: the money itself
// is sent by hand through QPay/SocialPay/POS before the admin presses save.
const ResolvePrintError = ({
  printError,
  setShowModal,
  onConflict,
  resolvePrintError,
}) => {
  const fromBundle = printError.payment_type === "bundle";
  // A team photo printed from a bundle: all its photos go back, nothing to type
  const photosBack = fromBundle && printError.refund_photos > 0;
  // null: the payment was not found, so the amount has to be typed in. For a bundle
  // print it is pages, not money. Without the kiosk's count it is the customer's count.
  const suggestion = suggestedRefundOf(printError);
  const byCustomer = !fromBundle && suggestion.customerPrinted !== null;
  const suggested = fromBundle ? pagesOfUnits(printError.refund_units) : suggestion.amount;
  // The backend wants a note for any amount other than its own, the customer's count too
  const backendAmount = printError.refund_amount ?? null;
  const [action, setAction] = useState("refunded");
  const [amount, setAmount] = useState(
    suggested === null ? "" : String(suggested)
  );
  const [note, setNote] = useState(
    byCustomer ? customerCountNote(printError, suggestion.customerPrinted) : ""
  );
  const [submitting, setSubmitting] = useState(false);

  const refunding = action === "refunded";
  const amountNumber = Number(amount);
  const amountValid = photosBack
    ? true
    : fromBundle
    ? /^\d+([.,]5)?$/.test(amount) && Number(amount.replace(",", ".")) > 0
    : /^\d+$/.test(amount) &&
      amountNumber > 0 &&
      (printError.amount === undefined || amountNumber <= printError.amount);
  const pagesNumber = Number(String(amount).replace(",", "."));
  const amountChanged = photosBack
    ? false
    : fromBundle
    ? suggested === null || pagesNumber !== suggested
    : backendAmount === null || amountNumber !== backendAmount;
  const noteRequired = refunding && amountChanged;
  const noteMissing = noteRequired && note.trim() === "";
  const canSubmit =
    !submitting && (!refunding || (amountValid && !noteMissing));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const body = { action };
    if (refunding && photosBack) body.refund_photos = printError.refund_photos;
    else if (refunding && fromBundle) body.refund_units = Math.round(pagesNumber * 2);
    else if (refunding) body.refund_amount = amountNumber;
    if (note.trim() !== "") body.note = note.trim();
    const result = await resolvePrintError(printError._id, body);
    if (result === "ok") {
      setShowModal(false);
    } else if (result === "conflict") {
      // Someone else decided first, or the payment was refunded elsewhere
      setShowModal(false);
      onConflict();
    } else {
      setSubmitting(false);
    }
  };

  const status = statusOf(printError);
  // Left by the customer through the kiosk's QR, and their account if they were
  // logged in while uploading
  const contact = printError.contact || {};
  const user = printError.user;
  // The customer's own count of the pages that printed correctly, and what it comes to
  const customerPrinted = customerPrintedOf(printError);
  // Not needed when the suggestion is the customer's count already
  const fromCount =
    fromBundle || byCustomer ? null : refundFromCount(printError, customerPrinted);
  const order = orderText(printError);
  const useCustomerCount = () => {
    setAction("refunded");
    setAmount(String(fromCount));
    if (note.trim() === "") setNote(customerCountNote(printError, customerPrinted));
  };
  return (
    <>
      {/* Only a press on the backdrop itself closes the sheet */}
      <div
        className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !submitting) {
            setShowModal(false);
          }
        }}
      >
        <div className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-xl">
          <header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div className="min-w-0">
              <p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
                {errorCodeLabel(printError.error_code)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {printError.printerName || printError.printerId}
                {printError.location ? " · " + printError.location : ""}
                {" · "}
                {dateText(printError.date)}
              </p>
            </div>
            <div className="flex items-center flex-shrink-0 ml-2">
              <span
                className={
                  "px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
                  status.badge
                }
              >
                {status.label}
              </span>
              <button
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
                aria-label="close"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  role="img"
                  aria-hidden="true"
                >
                  <path
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                    fillRule="evenodd"
                  ></path>
                </svg>
              </button>
            </div>
          </header>

          <div className="min-h-0 px-6 py-4 overflow-y-auto">
            {printError.error_message && (
              <div className="p-3 bg-red-100 rounded-md dark:bg-red-900">
                <h2 className="text-sm font-semibold text-red-700 dark:text-red-100">
                  Киоскийн мэдэгдсэн алдаа
                </h2>
                <p className="mt-1 overflow-y-auto text-sm text-gray-700 break-words max-h-36 dark:text-gray-300">
                  {printError.error_message}
                </p>
              </div>
            )}

            {!printError.paymentLogId && (
              <div className="p-3 mt-4 text-sm text-yellow-700 bg-yellow-100 rounded-md dark:bg-yellow-700 dark:text-yellow-100">
                Төлбөр олдсонгүй: энэ invoice-оор төлсөн төлбөрийн бичлэг
                системд алга.
                {suggested === null &&
                  " Буцаах дүн болон тайлбарыг гараар оруулна уу."}
              </div>
            )}

            <div className="mt-6 text-sm">
              <h2 className="font-semibold text-gray-700 dark:text-gray-400">
                Дэлгэрэнгүй
              </h2>
              {detailRow("Invoice", printError.invoice || "—")}
              {detailRow("Төлбөрийн төрөл", printError.payment_type || "—")}
              {detailRow("Хуудас (хэвлэгдсэн / нийт)", pagesText(printError))}
              {order && detailRow("Захиалга", order)}
              {fromBundle
                ? detailRow("Төлбөр", "Багцаас")
                : detailRow("Төлсөн дүн", money(printError.amount))}
              {detailRow(
                "Санал болгож буй буцаалт",
                fromBundle ? (
                  pagesLabel(suggested)
                ) : (
                  <>
                    {money(suggested)}
                    {byCustomer && (
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Хэрэглэгчийн тоогоор, киоск тоолоогүй
                      </span>
                    )}
                  </>
                )
              )}
              {detailRow("Файл", fileOf(printError))}
              {detailRow("Event ID", printError.event_id || "—")}
            </div>

            <div className="mt-6 text-sm">
              <h2 className="font-semibold text-gray-700 dark:text-gray-400">
                Хэрэглэгч
              </h2>
              {contact.phone && (
                <>
                  {detailRow("Утас (QR-аар)", phoneLink(contact.phone))}
                  {customerPrinted !== null &&
                    detailRow(
                      "Зөв хэвлэгдсэн (хэрэглэгчийн хэлснээр)",
                      customerPrinted + " / " + (printError.pages ?? "—")
                    )}
                  {fromCount !== null &&
                    detailRow(
                      "Энэ тоогоор буцаах",
                      fromCount > 0 ? (
                        <>
                          {money(fromCount)}
                          <button
                            type="button"
                            onClick={useCustomerCount}
                            disabled={submitting}
                            className="ml-2 text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline"
                          >
                            Оруулах
                          </button>
                        </>
                      ) : (
                        "Бүгд хэвлэгдсэн гэж мэдэгдсэн"
                      )
                    )}
                  {contact.account_number && (
                    <>
                      {/* The IBAN names the bank; only older contacts carry one. */}
                      {contact.bank && detailRow("Банк", contact.bank)}
                      {detailRow(
                        accountLabel(contact.account_number),
                        <>
                          {accountText(contact.account_number)}
                          <CopyButton value={contact.account_number} />
                        </>
                      )}
                      {detailRow("Данс эзэмшигч", contact.account_name)}
                    </>
                  )}
                  {detailRow("Илгээсэн", dateText(contact.date))}
                </>
              )}
              {user && (
                <>
                  {detailRow("Бүртгэлтэй хэрэглэгч", user.name)}
                  {detailRow("Утас", user.phone ? phoneLink(user.phone) : "—")}
                  {detailRow("Имэйл", user.email)}
                </>
              )}
              {!contact.phone && !user && (
                <p className="mt-1 text-gray-500 dark:text-gray-400">
                  Хэрэглэгч холбоо барих мэдээлэл үлдээгээгүй байна. Киоск
                  дээрх QR-аар үлдээвэл энд харагдана.
                </p>
              )}
            </div>

            <div className="mt-6 text-sm">
              <h2 className="font-semibold text-gray-700 dark:text-gray-400">
                Шийдвэр
              </h2>
              {ACTIONS.map((option) => (
                <label
                  key={option.key}
                  className="flex items-start mt-3 text-gray-700 dark:text-gray-400"
                >
                  <input
                    type="radio"
                    name="action"
                    value={option.key}
                    checked={action === option.key}
                    onChange={() => setAction(option.key)}
                    disabled={submitting}
                    className="mt-1 text-purple-600 focus:ring-purple-200 focus:ring-opacity-50 focus:ring"
                  ></input>
                  <span className="ml-2">
                    <span className="font-medium">{option.label}</span>
                    <span className="block text-gray-500 dark:text-gray-500">
                      {fromBundle && option.key === "refunded"
                        ? BUNDLE_REFUND_HELP
                        : option.help}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {refunding && photosBack && (
              <p className="mt-4 text-sm text-gray-700 dark:text-gray-400">
                Багийн зургийн хэвлэлт: <b>{printError.refund_photos} зураг</b> багцад нь буцна.
              </p>
            )}
            {refunding && !photosBack && (
              <label className="block mt-4 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  {fromBundle ? "Буцаах хуудас" : "Буцаасан дүн (₮)"}
                </span>
                <input
                  type="number"
                  min={fromBundle ? "0.5" : "1"}
                  max={fromBundle ? undefined : printError.amount}
                  step={fromBundle ? "0.5" : "1"}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={submitting}
                  className={inputClass}
                />
                {amount !== "" && !amountValid && (
                  <span className="block mt-1 text-xs text-red-600 dark:text-red-400">
                    {fromBundle
                      ? "Хуудас 0,5-аар нэмэгдэх эерэг тоо байна (өнгөт хуудас 1,5)."
                      : `Дүн 1-ээс ${money(printError.amount)} хүртэлх бүхэл тоо байна.`}
                  </span>
                )}
              </label>
            )}

            <label className="block mt-4 text-sm">
              <span className="text-gray-700 dark:text-gray-400">
                Тайлбар
                {noteRequired ? (
                  <span className="text-red-600 dark:text-red-400"> *</span>
                ) : (
                  " (заавал биш)"
                )}
              </span>
              <textarea
                rows="3"
                maxLength={NOTE_MAX}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={submitting}
                className={inputClass}
              ></textarea>
              <span className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span className={noteMissing ? "text-red-600 dark:text-red-400" : ""}>
                  {noteRequired
                    ? suggested === null
                      ? "Төлбөр олдоогүй тул тайлбар заавал бичнэ."
                      : byCustomer && amountNumber === suggested
                      ? "Хэрэглэгчийн тоогоор буцаавал тайлбар заавал бичнэ."
                      : "Санал болгосон дүнгээс өөр дүн буцаавал тайлбар заавал бичнэ."
                    : ""}
                </span>
                <span>
                  {note.length}/{NOTE_MAX}
                </span>
              </span>
            </label>

            {refunding && fromBundle && (
              <p className="p-3 mt-4 text-sm text-purple-700 bg-purple-100 rounded-md dark:bg-gray-700 dark:text-purple-200">
                Хадгалахад хуудас шууд багцад нь буцаж орно. Багцын хугацаа дууссан
                эсвэл 7 хоногоос бага үлдсэн бол 7 хоногоор сунгана. Буцаалтыг
                цуцлах боломжгүй.
              </p>
            )}
            {refunding && !fromBundle && (
              <p className="p-3 mt-4 text-sm text-purple-700 bg-purple-100 rounded-md dark:bg-gray-700 dark:text-purple-200">
                Энэ товч зөвхөн системд тэмдэглэнэ, мөнгө шилжүүлэхгүй. Мөнгийг
                хэрэглэгчийн данс руу эсвэл QPay/SocialPay/POS-оор гараар
                шилжүүлсний дараа дарна уу.
              </p>
            )}
          </div>

          <footer className="flex justify-end flex-shrink-0 px-6 py-3 space-x-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <button
              onClick={() => setShowModal(false)}
              disabled={submitting}
              className={secondaryButton}
            >
              Болих
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className={
                primaryButton + (canSubmit ? "" : " opacity-50 cursor-not-allowed")
              }
            >
              {submitting ? "Хадгалж байна..." : "Хадгалах"}
            </button>
          </footer>
        </div>
      </div>
    </>
  );
};

ResolvePrintError.propTypes = {
  printError: PropTypes.object.isRequired,
  setShowModal: PropTypes.func.isRequired,
  onConflict: PropTypes.func.isRequired,
  resolvePrintError: PropTypes.func.isRequired,
};

export default connect(null, { resolvePrintError })(ResolvePrintError);
